<script setup lang="ts">
import { computed } from "vue";

const route = useRoute();
const { siteUrl } = useRuntimeConfig().public;
const site = useSite();

let data: Ref<Record<string, any> | null>;
try {
  const result = await useKirbyData(route.path, {
    transform: (page: Record<string, any>) => {
      const slim = slimPagePayload(page) as Record<string, any>;
      if (!slim || typeof slim !== "object") return slim;

      // Trek hub maps are ClientOnly: load markers via /api/map-data on mount
      if (slim.template === "treks" && "mapData" in slim) {
        const { mapData: _mapData, ...rest } = slim;
        if (Array.isArray(rest.trekPage)) {
          rest.trekPage = rest.trekPage.map(
            (item: Record<string, unknown>) => ({
              uri: item.uri,
              id: item.id,
              slug: item.slug,
              title: item.title,
              menuText: item.menuText,
              template: item.template,
              isListed: item.isListed,
            }),
          );
        }
        return rest;
      }

      // Itinerary (template=trek) pages: drop mapData / quiz blobs and keep
      // only this trek's packages & prices so __NUXT_DATA__ stays small.
      if (slim.template === "trek") {
        return slimTrekItineraryPage(slim);
      }

      return slim;
    },
  });
  data = result.data as Ref<Record<string, any> | null>;
} catch (error: unknown) {
  const statusCode =
    (error as { statusCode?: number; status?: number })?.statusCode ||
    (error as { statusCode?: number; status?: number })?.status ||
    404;
  throw createError({
    statusCode: statusCode === 404 ? 404 : 502,
    statusMessage: statusCode === 404 ? "Page not found" : "Failed to load page",
    fatal: true,
  });
}

requirePageData(data.value);
setPage(data.value);

// Create a computed property for safe data access
const pageData = computed(() => data?.value || null);

const isFlagshipTrekHub = computed(() => {
  if (!pageData.value) return false;
  if (pageData.value.template !== "treks") return false;
  return isFlagshipTrekPath(route.path);
});

const isMapPage = computed(() => pageData.value?.template === "map-page");

// Feefo on flagship hubs (JSON-LD)
const { data: feefoData } = await useAsyncData(
  `feefo-flagship-${route.path}`,
  async () => {
    if (!isFlagshipTrekPath(route.path)) return null;
    if (pageData.value?.template !== "treks") {
      return null;
    }
    try {
      return await $fetch("/api/feefo");
    } catch {
      return null;
    }
  },
);

const isTrekHub = computed(() => pageData.value?.template === "treks");

/** Resolve FAQ child path for any trek hub (e.g. tour-du-mont-blanc/…-faqs, patagonia-hiking/patagonia-faqs). */
function resolveTrekFaqPath(page: Record<string, unknown> | null): string | null {
  if (!page) return null;
  const pageUri = String(page.uri ?? "").replace(/^\/+|\/+$/g, "");
  if (!pageUri) return null;
  const uriPrefix = `${pageUri}/`;

  const trekPages = Array.isArray(page.trekPage) ? page.trekPage : [];
  const child = trekPages.find(
    (item: Record<string, unknown>) =>
      item?.template === "faqs" &&
      String(item?.uri ?? "").startsWith(uriPrefix),
  );
  if (child?.uri) return String(child.uri).replace(/^\/+/, "");

  // Site nav is reliable for worldwide hubs whose FAQ slug differs from {uri}-faqs
  const siteChildren = Array.isArray(site.value?.children) ? site.value.children : [];
  const hub = siteChildren.find(
    (item: Record<string, unknown>) => String(item?.uri ?? "") === pageUri,
  );
  const subPages = Array.isArray(hub?.subPages) ? hub.subPages : [];
  const navFaq = subPages.find(
    (item: Record<string, unknown>) => item?.template === "faqs",
  );
  if (navFaq?.uri) return String(navFaq.uri).replace(/^\/+/, "");

  // Hub is in nav but has no FAQ child — skip fetch
  if (hub) return null;

  // Convention fallback when hub is missing from slimmed site nav
  return `${pageUri}/${pageUri}-faqs`;
}

const { data: trekHubFaqPage } = await useAsyncData(
  `trek-hub-faqs-${route.path}`,
  async () => {
    const page = data?.value as Record<string, unknown> | null;
    if (!page || page.template !== "treks") {
      return null;
    }
    const faqPath = resolveTrekFaqPath(page);
    if (!faqPath) return null;
    try {
      return await $kirby(faqPath);
    } catch (error) {
      console.warn(`[trek FAQ] Failed to load ${faqPath}`, error);
      return null;
    }
  },
);

/** Parent hub payload for map-page key facts (stats live on the trek hub, not map-page). */
const { data: mapPageParentHub } = await useAsyncData(
  `map-page-parent-hub-${route.path}`,
  async () => {
    const page = data?.value as Record<string, unknown> | null;
    if (!page || page.template !== "map-page") return null;
    const parentUri = String(page.parentPageUri ?? "")
      .replace(/^\/+|\/+$/g, "");
    if (!parentUri) return null;
    try {
      return await $kirby(parentUri);
    } catch (error) {
      console.warn(`[map page] Failed to load parent hub ${parentUri}`, error);
      return null;
    }
  },
);

/** FAQ child for map-page map-intent accordion. */
const { data: mapPageFaqPage } = await useAsyncData(
  `map-page-faqs-${route.path}`,
  async () => {
    const page = data?.value as Record<string, unknown> | null;
    if (!page || page.template !== "map-page") return null;
    const parentUri = String(page.parentPageUri ?? "")
      .replace(/^\/+|\/+$/g, "");
    if (!parentUri) return null;
    const faqPath = resolveTrekFaqPath({
      uri: parentUri,
      trekPage: page.trekPage,
    });
    if (!faqPath) return null;
    try {
      return await $kirby(faqPath);
    } catch (error) {
      console.warn(`[map page FAQ] Failed to load ${faqPath}`, error);
      return null;
    }
  },
);

function flagshipPricesFor(field: "trekprices1" | "trekprices2") {
  const page = pageData.value;
  if (!page || !isFlagshipTrekHub.value) return [];
  const raw = Array.isArray(page[field]) ? page[field] : [];
  const title = String(page.title ?? "")
    .toLowerCase()
    .replace(/['']/g, "");
  return raw.filter((trek: Record<string, unknown>) => {
    const routeName = String(trek?.trek_route ?? "")
      .toLowerCase()
      .replace(/['']/g, "");
    if (!routeName) return false;
    return title.includes(routeName) || routeName.includes(title);
  });
}

const flagshipTrekprices = computed(() => flagshipPricesFor("trekprices1"));
const flagshipTrekprices2 = computed(() => flagshipPricesFor("trekprices2"));

/** Resolve the FAQ page payload (cats + faqs) for trek hub display. */
const trekHubFaqSource = computed(() => {
  const page = pageData.value;
  if (!page || !isTrekHub.value) return null;

  const fetched = trekHubFaqPage.value as Record<string, unknown> | null;
  if (
    fetched &&
    Array.isArray(fetched.faqCats) &&
    fetched.faqCats.length &&
    Array.isArray(fetched.faqs) &&
    fetched.faqs.length
  ) {
    return fetched;
  }

  const pageUri = String(page.uri ?? "");
  const uriPrefix = `${pageUri}/`;
  const faqPages = Array.isArray(page.faqPage) ? page.faqPage : [];
  const match =
    faqPages.find(
      (item: Record<string, unknown>) =>
        String(item?.parentUri ?? "").startsWith(pageUri) ||
        String(item?.uri ?? "").startsWith(uriPrefix) ||
        String(item?.uri ?? "") === pageUri,
    ) ?? null;
  if (match && Array.isArray(match.faqCats) && Array.isArray(match.faqs)) {
    return match;
  }

  const trekPages = Array.isArray(page.trekPage) ? page.trekPage : [];
  const faqChild = trekPages.find(
    (item: Record<string, unknown>) =>
      item?.template === "faqs" &&
      String(item?.uri ?? "").startsWith(uriPrefix),
  );
  if (
    faqChild &&
    Array.isArray(faqChild.faqCats) &&
    Array.isArray(faqChild.faqs)
  ) {
    return faqChild;
  }

  return null;
});

/** Category-grouped FAQs matching /{trek}-faqs (AppTrekFaqs) layout. */
const trekHubGroupedFaqs = computed(() => {
  const source = trekHubFaqSource.value;
  if (!source) return {} as Record<string, { faqs: any[]; catIcon: string }>;

  const cats = Array.isArray(source.faqCats) ? source.faqCats : [];
  const faqs = Array.isArray(source.faqs) ? source.faqs : [];
  const grouped: Record<string, { faqs: any[]; catIcon: string }> = {};

  for (const cat of cats as Array<{ category?: string; catIcon?: string }>) {
    const category = String(cat?.category ?? "").trim();
    if (!category) continue;
    grouped[category] = {
      faqs: faqs.filter((item: any) =>
        sanitizeString(String(item?.categories ?? "")).includes(
          sanitizeString(category),
        ),
      ),
      catIcon: String(cat?.catIcon ?? ""),
    };
  }

  return grouped;
});

const flagshipFaqs = computed(() => {
  if (!isFlagshipTrekHub.value) return [];
  // Same Q&A set as the visible trek hub FAQ accordion (grouped by category)
  const grouped = trekHubGroupedFaqs.value;
  const flat = Object.values(grouped).flatMap((group) =>
    Array.isArray(group?.faqs) ? group.faqs : [],
  );
  return flattenFaqItems({ faqs: flat as never });
});

const flagshipItinerary = computed(() =>
  resolveTrekItineraryDays(
    pageData.value,
    Array.isArray(pageData.value?.trekPageTrek)
      ? pageData.value.trekPageTrek
      : null,
  ),
);

// Function to sanitize strings by removing apostrophes and normalizing for comparison
const sanitizeString = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/['`]/g, "") // Remove apostrophes and backticks
    .replace(/[^\w\s]/g, "") // Remove other special characters except letters, numbers, spaces
    .trim();
};

// Compute FAQs only if the template is 'faqs'
const faqs = computed(() =>
  pageData.value?.template === "faqs" ? (pageData.value?.faqs ?? []) : [],
);
const faqCats = computed(() =>
  pageData.value?.template === "faqs" ? (pageData.value?.faqCats ?? []) : [],
);

// Group FAQs by category
const filteredFaqs = computed(() => {
  const grouped: Record<string, { faqs: any[]; catIcon: string }> = {};

  faqCats.value.forEach((cat: any) => {
    grouped[cat.category] = {
      faqs: faqs.value.filter((item: any) =>
        sanitizeString(item.categories).includes(sanitizeString(cat.category)),
      ),
      catIcon: cat.catIcon, // Store catIcon for each category
    };
  });

  return grouped;
});

const flatFaqs = computed(() =>
  Object.values(filteredFaqs.value).flatMap((faqData) => faqData.faqs),
);

const KEY_FACT_ICONS: Record<string, string> = {
  Distance: "Distance",
  "Height gain": "Ascent Descent",
  Grade: "Trek Grade 3",
  "Duration / stages": "Duration",
  "Best months": "Dates",
  "Group size": "Group size",
};

function iconForGrade(grade: unknown): string {
  const match = String(grade ?? "").match(/([1-4])/);
  return match ? `Trek Grade ${match[1]}` : "Trek Grade 3";
}

const mapPageKeyFacts = computed(() => {
  if (!isMapPage.value) return [] as Array<{ label: string; value: string; icon: string }>;
  const p = (mapPageParentHub.value ?? pageData.value) as Record<
    string,
    unknown
  > | null;
  if (!p) return [];

  const facts: Array<{ label: string; value: string; icon: string }> = [];
  const add = (label: string, value: unknown, icon?: string) => {
    const str = String(value ?? "").trim();
    if (str) {
      facts.push({
        label,
        value: str,
        icon: icon || KEY_FACT_ICONS[label] || "Info",
      });
    }
  };

  add("Distance", p.trek_distance);
  add("Height gain", p.trek_elevation);
  add("Grade", p.trek_grade, iconForGrade(p.trek_grade));
  add("Duration / stages", p.trek_duration || p.duration || p.trekDuration);
  add("Best months", p.trek_season || p.bestMonths || p.trekBestMonths);
  add("Group size", p.group_sizes || p.groupSize || p.trek_group_size);

  const coveredIcons = new Set<string>();
  if (p.trek_distance) coveredIcons.add("Distance");
  if (p.trek_elevation) coveredIcons.add("Ascent Descent");
  if (p.trek_grade) coveredIcons.add(iconForGrade(p.trek_grade));

  if (Array.isArray(p.trekIntroStats)) {
    for (const stat of p.trekIntroStats as Array<{
      statText?: string;
      statIcon?: string;
    }>) {
      const text = String(stat?.statText ?? "").trim();
      const icon = String(stat?.statIcon ?? "").trim();
      if (!text) continue;
      if (icon && coveredIcons.has(icon)) continue;
      const alreadyHave = facts.some(
        (f) =>
          f.value === text ||
          f.value.toLowerCase().includes(text.toLowerCase()) ||
          text.toLowerCase().includes(f.value.toLowerCase()),
      );
      if (alreadyHave) continue;
      facts.push({
        label: icon || "Fact",
        value: text,
        icon: icon || "Info",
      });
    }
  }

  return facts;
});

const mapPageFaqItems = computed(() => {
  const source = mapPageFaqPage.value as Record<string, unknown> | null;
  if (!source || !Array.isArray(source.faqs)) return [];
  return source.faqs as Array<{
    question?: string;
    title?: string;
    answer?: string;
    text1?: string;
  }>;
});

useJsonLdHead(
  computed(() => {
    if (!pageData.value) return null;

    if (isFlagshipTrekHub.value) {
      return buildFlagshipTrekGraphJsonLd(
        pageData.value,
        route.path,
        siteUrl,
        {
          trekprices: flagshipTrekprices.value,
          trekprices2: flagshipTrekprices2.value,
          site: site.value,
          itinerary: flagshipItinerary.value,
          stats: resolveTrekStats(pageData.value, site.value),
          faqs: flagshipFaqs.value,
          feefoData: feefoData.value,
        },
      );
    }

    // Itinerary pages (template=trek): TouristTrip with day-by-day ItemList,
    // GBP offers + dates, and TravelAgency provider.
    if (pageData.value.template === "trek") {
      return withBreadcrumbJsonLd(
        buildTouristTripJsonLd(pageData.value, route.path, siteUrl, {
          trekprices: filterTrekPriceRows(
            Array.isArray(pageData.value.trekprices1)
              ? pageData.value.trekprices1
              : [],
            pageData.value,
          ),
          trekprices2: filterTrekPriceRows(
            Array.isArray(pageData.value.trekprices2)
              ? pageData.value.trekprices2
              : [],
            pageData.value,
          ),
          site: site.value,
          itinerary: resolveTrekItineraryDays(pageData.value),
          stats: resolveTrekStats(pageData.value, site.value),
        }),
        route.path,
        siteUrl,
      );
    }

    const type = pageData.value?.meta?.structuredDataType || "WebPage";
    const primary =
      type === "FAQPage"
        ? buildFaqPageJsonLd(
            pageData.value,
            route.path,
            siteUrl,
            flatFaqs.value,
          )
        : buildWebPageJsonLd(pageData.value, route.path, siteUrl);

    return withBreadcrumbJsonLd(primary, route.path, siteUrl);
  }),
);

useHead({
  bodyAttrs: {
    class: [
      `${data?.value?.template}-page`,
      data?.value?.trekLocation
        ? `${data?.value.trekLocation.toLowerCase().replace(/\s+/g, "-")}-page`
        : "",
      data?.value?.parentTrekLocation
        ? `${data?.value.parentTrekLocation.toLowerCase().replace(/\s+/g, "-")}-page`
        : "",
    ]
      .filter(Boolean)
      .join(" "),
  },
});

definePageMeta({
  layout: "default",
});

const urlPathLevel = computed(() => {
  const pathSegments = route.path.split("/").filter(Boolean);
  return pathSegments.length; // Adjust if needed based on your URL structure
});
</script>

<template>
  <div>
    <AppHeading1 />
    <AppHeroImage v-if="data?.heroType === 'Hero Image'" />
    <AppHeroVideo v-if="data?.heroType === 'Hero Video'" />
    <div
      class="content-wrapper"
      :class="{
        'treks-page': data?.template === 'treks',
        'trek-page': data?.template === 'trek',
      }"
    >
      <!-- Template: page or what-to-eat -->
      <div
        v-if="data?.template === 'page' || data?.template === 'what-to-eat'"
        class="page-wrapper"
      >
        <div class="content">
          <AppNavBreadcrumb />
          <KirbyLayouts :layouts="data?.layouts ?? []" />
        </div>
        <section
          v-if="
            data?.template === 'page' ||
            data?.parentPageTemplate === 'treks' ||
            data?.template === 'what-to-eat'
          "
          class="right-col"
        >
          <AppTrekUsefulInfo
            v-if="urlPathLevel === 1"
            :trek-page="data?.trekPage"
            :trek-uri="data?.uri"
          />
          <AppTrekUsefulInfo
            v-if="urlPathLevel === 2"
            :trek-page="data?.trekPage"
            :trek-uri="data?.parentPageUri"
          />
        </section>
      </div>

      <!-- Template: treks -->

      <AppTreksContent
        v-if="data?.template === 'treks'"
        :flagship-grouped-faqs="isTrekHub ? trekHubGroupedFaqs : {}"
        :layouts="data?.layouts"
      />

      <!-- Template: trek -->

      <AppTrekContent v-if="data?.template === 'trek'" />

      <!-- Template: faqs -->
      <div v-if="data?.template === 'faqs'">
        <div class="content">
          <AppNavBreadcrumb />
          <KirbyLayouts :layouts="data?.layouts ?? []" />
          <AppTrekFaqs :faqs="filteredFaqs" />
        </div>
      </div>

      <!-- Template: map-page -->
      <div v-if="data?.template === 'map-page'" id="maps">
        <div class="map-page-wrapper">
          <div class="container">
            <AppNavBreadcrumb />

            <div class="map-box-wrap">
              <div class="map-box-info">
                <h2>{{ data?.map1title }}</h2>
                <div v-html="data?.map1text" />
                <AppMapPageRoutePackage
                  :trek-title="data?.parentPageTitle"
                  variant="full"
                  :fallback-link="data?.map1link"
                />
              </div>
              <div class="map-box-section">
                <AppMapsMapboxShell
                  :trek-title="`${data?.parentPageTitle} Full Route`"
                  :trek-title-short="`${data?.parentPageTitle}`"
                  :trek-page="true"
                />
              </div>
            </div>

            <div class="map-plan-block">
              <div class="map-brand-graphic">
                <AppSVGBrandGraphic />
              </div>
              <AppMapPageAvailabilityStrip
                :trek-title="data?.parentPageTitle"
                variant="full"
              />
            </div>

            <div v-if="data?.map2title" class="map-box-wrap">
              <div class="map-box-info">
                <h2>{{ data?.map2title }}</h2>
                <div v-html="data?.map2text" />
                <AppMapPageRoutePackage
                  :trek-title="data?.parentPageTitle"
                  variant="westerly"
                  :fallback-link="data?.map2link"
                />
              </div>
              <div class="map-box-section">
                <AppMapsMapboxShell
                  :trek-title="`${data?.parentPageTitle} Westerly Route`"
                  :trek-page="true"
                />
              </div>
            </div>

            <div v-if="data?.map2title" class="map-plan-block">
              <div class="map-brand-graphic">
                <AppSVGBrandGraphic />
              </div>
              <AppMapPageAvailabilityStrip
                :trek-title="data?.parentPageTitle"
                variant="westerly"
              />
            </div>

            <div v-if="data?.map3title" class="map-box-wrap">
              <div class="map-box-info">
                <h2>{{ data?.map3title }}</h2>
                <div v-html="data?.map3text" />
                <AppMapPageRoutePackage
                  :trek-title="data?.parentPageTitle"
                  variant="easterly"
                  :fallback-link="data?.map3link"
                />
              </div>
              <div class="map-box-section">
                <AppMapsMapboxShell
                  :trek-title="`${data?.parentPageTitle} Easterly Route`"
                  :trek-id="1"
                  :trek-page="true"
                />
              </div>
            </div>

            <div v-if="data?.map3title" class="map-plan-block">
              <div class="map-brand-graphic">
                <AppSVGBrandGraphic />
              </div>
              <AppMapPageAvailabilityStrip
                :trek-title="data?.parentPageTitle"
                variant="easterly"
              />
            </div>

            <ClientOnly>
              <AppMapsTmbProfile
                v-if="data?.parentPageTitle === 'Tour du Mont Blanc'"
              />
              <AppMapsWhrProfile
                v-if="data?.parentPageTitle === 'Walker\'s Haute Route'"
              />
              <AppMapsDolomitesProfile
                v-if="data?.parentPageTitle === 'The Italian Dolomites'"
              />

              <AppMapsJungfrauProfile
                v-if="data?.parentPageTitle === 'Tour of the Jungfrau'"
              />

              <AppMapsPatagoniaProfile
                v-if="data?.parentPageTitle === 'Patagonia Hiking'"
              />
            </ClientOnly>

            <AppMapPageKeyFacts :facts="mapPageKeyFacts" />
          </div>
        </div>

        <div class="map-page-lower">
          <div class="container">
            <section class="useful-info-nav-wrap">
              <AppTrekUsefulInfo
                v-if="urlPathLevel === 2"
                :trek-page="data?.trekPage"
                :trek-uri="data?.parentPageUri"
              />
            </section>

            <AppMapPageFaqs
              :parent-page-title="data?.parentPageTitle"
              :faqs="mapPageFaqItems"
            />

            <section id="trekPackages" class="trek-packages">
              <header>
                <h3>Trek Packages</h3>
              </header>
              <AppTrekPackagesApi :trek-title="data?.parentPageTitle" />
            </section>
          </div>
        </div>
      </div>
      <!-- Template: fitness-preparation -->
      <template v-if="data?.template === 'fitness-preparation'">
        <section class="page-intro-section">
          <section class="content">
            <AppNavBreadcrumb />
            <KirbyLayouts v-if="data?.layouts" :layouts="data?.layouts ?? []" />
          </section>
        </section>
        <section class="content">
          <ClientOnly>
            <AppFitnessTips />
          </ClientOnly>
        </section>
        <ClientOnly>
          <AppQuizGrading />
        </ClientOnly>
        <section class="content">
          <ClientOnly>
            <AppNutrition />
          </ClientOnly>

          <AppTrekUsefulInfo
            :trek-page="data?.trekPage"
            :trek-uri="data?.parentPageUri"
          />
        </section>
      </template>

      <!-- Template: where-to-stay -->
      <div v-if="data?.template === 'where-to-stay'" class="content">
        <AppNavBreadcrumb />
        <KirbyLayouts :layouts="data?.layouts ?? []" />
        <AppAccommodation
          v-if="data?.parentPageTemplate === 'treks'"
          :trek-title="data?.parentPageTitle"
        />
        <AppAccommodationAll v-else />
        <section
          v-if="data?.parentPageTemplate === 'treks'"
          id="trekUsefulInfo"
        >
          <AppTrekUsefulInfo
            :trek-page="data?.trekPage"
            :trek-uri="data?.parentPageUri"
          />
        </section>
      </div>

      <!-- Template: what-to-bring -->
      <template v-if="data?.template === 'what-to-bring'">
        <section class="page-intro-section">
          <section class="content">
            <AppNavBreadcrumb />
            <KirbyLayouts :layouts="data?.layouts ?? []" />
          </section>
        </section>
        <section class="content">
          <AppKitList />
          <section
            v-if="data?.parentPageTemplate === 'treks'"
            id="trekUsefulInfo"
          ></section>
          <AppTrekUsefulInfo
            :trek-page="data?.trekPage"
            :trek-uri="data?.parentPageUri"
          />
        </section>
      </template>

      <!-- Template: default -->
      <div
        v-if="data?.template === 'default'"
        class="content"
      >
        <KirbyLayouts :layouts="data?.layouts ?? []" />
      </div>

      <!-- Template: Legal -->
      <div v-if="data?.template === 'legal'" class="content">
        <KirbyLayouts :layouts="data?.layouts ?? []" />
      </div>
    </div>
  </div>
</template>
